'use client';

import { useCallback, useState } from 'react';
import { APIProvider, Map, MapCameraChangedEvent } from '@vis.gl/react-google-maps';
import classNames from 'classnames';
import FeedbackIconLayer from 'components/gruenland/atoms/button/feedbackIconLayer';
import { Container } from 'components/gruenland/atoms/container/Container';
import { useFormat } from 'helpers/hooks/useFormat';
import useLanguage from 'helpers/hooks/useLanguage';
import { TYPOGRAPHY_PAGE_TITLE_2 } from 'lib/typography';
import MapHandler from './MapHandler';
import PlaceAutocomplete from './PlaceAutocomplete';
import StoresList from './StoresList';
import StoresMarkers from './StoresMarkers';
import { Store } from './types';
import useGoogleMapsApiKey from './useGoogleMapsApiKey';
import useStores from './useStores';

type Props = {
  productId?: string;
};

const DEFAULT_BOUNDS: React.ComponentProps<typeof Map>['defaultBounds'] = {
  west: 6,
  east: 19,
  north: 47,
  south: 37,
  padding: 0,
};

export const StoreLocator: React.FC<Props> = ({ productId }) => {
  const apikey = useGoogleMapsApiKey();
  const { stores } = useStores({ productId });

  const { formatMessage } = useFormat({ name: 'gruenland' });
  const [language] = useLanguage();

  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.PlaceResult | null>(null);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);

  const [filteredStores, setFilteredStores] = useState<NonNullable<typeof stores>>([]);
  const [zoom, setZoom] = useState(0);

  const handleBoundsChanged = useCallback(
    ({ detail: { bounds } }: MapCameraChangedEvent) => {
      setFilteredStores(
        (stores || []).filter(
          (x) => x.lng >= bounds.west && x.lng <= bounds.east && x.lat >= bounds.south && x.lat <= bounds.north,
        ),
      );
    },
    [stores],
  );

  const handleZoomChanged = useCallback(({ detail: { zoom } }: MapCameraChangedEvent) => {
    setZoom(zoom);
  }, []);

  const showMarkers = zoom >= 9 || filteredStores.length < 50;

  if (!stores || !apikey) {
    return <FeedbackIconLayer loading />;
  }

  return (
    <Container>
      <APIProvider apiKey={apikey} language={language}>
        <div className="grid gap-5 lg:grid-cols-2">
          <div>
            <h2 className={classNames(TYPOGRAPHY_PAGE_TITLE_2, 'my-[10px]')}>
              {formatMessage({ id: 'storelocator.subtitle', defaultMessage: 'Trova il tuo store' })}
            </h2>
            <div className="my-[10px]">
              <PlaceAutocomplete onPlaceSelect={setSelectedPlace} />
            </div>
            <div>
              {formatMessage({
                id: 'storelocator.count',
                values: { count: filteredStores.length },
                defaultMessage: 'Sono presenti {count} negozi.',
              })}
            </div>
            <div className="flex flex-col gap-3 overflow-y-auto p-[5px] lg:h-[500px]">
              {showMarkers && <StoresList onStoreSelect={setSelectedStore} stores={filteredStores} />}
            </div>
          </div>
          <div className="min-h-[500px]">
            <Map
              defaultBounds={DEFAULT_BOUNDS}
              gestureHandling={'greedy'}
              disableDefaultUI={true}
              mapId="store-locator-map"
              minZoom={5}
              onBoundsChanged={handleBoundsChanged}
              onZoomChanged={handleZoomChanged}
            >
              {showMarkers && <StoresMarkers stores={filteredStores} />}
            </Map>

            <MapHandler place={selectedPlace} store={selectedStore} />
          </div>
        </div>
      </APIProvider>
    </Container>
  );
};
